import {useRef, useState} from "react";
import {FiArrowDownRight, FiArrowUpRight, FiCode, FiGitBranch, FiMapPin} from "react-icons/fi";
import {PROFILE, RESUME_PDF_PATH} from "../../data/resume";

const PROFILE_CODE = [
    <><span className="syntax-comment">{"// a little about the engineer"}</span></>,
    <><span className="syntax-keyword">const</span> engineer = {'{'}</>,
    <>  name: <span className="syntax-string">"{PROFILE.name}"</span>,</>,
    <>  role: <span className="syntax-string">"Full-Stack + Founding"</span>,</>,
    <>  focus: [</>,
    <>    <span className="syntax-string">"Product + frontend"</span>,</>,
    <>    <span className="syntax-string">"Backend + AI systems"</span>,</>,
    <>  ],</>,
    <>  stack: [</>,
    <>    <span className="syntax-string">"React"</span>, <span className="syntax-string">"Node"</span>, <span className="syntax-string">"Python"</span>,</>,
    <>  ],</>,
    <>  approach: <span className="syntax-string">"idea → production"</span>,</>,
    <>{'}'};</>,
    <> </>,
    <><span className="syntax-keyword">export default</span> engineer;</>,
];

const NOTEBOOK_TABS = [
    {id: "about", file: "about.ts", language: "TypeScript"},
    {id: "architecture", file: "architecture.md", language: "Markdown"},
    {id: "decisions", file: "decisions.md", language: "Markdown"},
] as const;

const EngineerNotebook = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const active = NOTEBOOK_TABS[activeIndex];
    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        let next = activeIndex;
        if (event.key === "ArrowRight") next = (activeIndex + 1) % NOTEBOOK_TABS.length;
        else if (event.key === "ArrowLeft") next = (activeIndex + NOTEBOOK_TABS.length - 1) % NOTEBOOK_TABS.length;
        else if (event.key === "Home") next = 0;
        else if (event.key === "End") next = NOTEBOOK_TABS.length - 1;
        else return;
        event.preventDefault();
        setActiveIndex(next);
        tabRefs.current[next]?.focus();
    };
    return <div className="profile-editor" role="region" aria-label="Engineering notebook">
        <div className="editor-chrome">
            <div className="editor-dots" aria-hidden="true"><i /><i /><i /></div>
            <span className="editor-window-title">jarrod / engineering-notes</span>
            <FiCode aria-hidden="true" />
        </div>
        <div className="editor-tabs" role="tablist" aria-label="Engineering notebook files">
            {NOTEBOOK_TABS.map((tab, index) => <button key={tab.id} ref={el => {tabRefs.current[index] = el;}} type="button" className="editor-tab" role="tab" id={`notebook-tab-${tab.id}`} aria-controls={`notebook-panel-${tab.id}`} aria-selected={index === activeIndex} tabIndex={index === activeIndex ? 0 : -1} onClick={() => setActiveIndex(index)} onKeyDown={handleKeyDown}>{tab.file}</button>)}
        </div>
        <div className="editor-breadcrumb" aria-hidden="true">src <span>/</span> portfolio <span>/</span> {active.file}</div>
        <div className="notebook-panel" role="tabpanel" id={`notebook-panel-${active.id}`} aria-labelledby={`notebook-tab-${active.id}`} tabIndex={0}>
            {active.id === "about" && <pre className="profile-code subtle-scrollbar"><code>{PROFILE_CODE.map((line, index) => <span className="code-line" key={index}><span className="line-number" aria-hidden="true">{index + 1}</span><span>{line}</span>{'\n'}</span>)}</code></pre>}
            {active.id === "architecture" && <div className="notebook-prose">
                <p className="notebook-kicker"># architecture</p>
                <h3>Where the boundaries go.</h3>
                <p className="notebook-lead">Three builds. Three ways to keep the core independent of the interface.</p>
                <ol className="notebook-notes">
                    <li><a href="#stock-drawer">Stock Drawer <FiArrowUpRight aria-hidden="true" /></a><p>Vue → Web Worker → matching engine</p><span>Search runs off the main thread.</span></li>
                    <li><a href="#engineering-evidence-explorer">Evidence Explorer <FiArrowUpRight aria-hidden="true" /></a><p>Browser + MCP → shared retrieval</p><span>One evidence engine, two interfaces.</span></li>
                    <li><a href="#road-test">Road Test <FiArrowUpRight aria-hidden="true" /></a><p>Python runner → JSON → Nuxt replay</p><span>Run locally. Inspect the saved evidence.</span></li>
                </ol>
            </div>}
            {active.id === "decisions" && <div className="notebook-prose">
                <p className="notebook-kicker"># decisions</p>
                <h3>The choices behind the code.</h3>
                <p className="notebook-lead">Trade-offs from the projects below.</p>
                <ol className="notebook-notes decision-notes">
                    <li><strong>Keep the original evidence.</strong><p>Stock Drawer matches normalized shapes, then replays the original prices.</p></li>
                    <li><strong>Make uncertainty visible.</strong><p>Evidence Explorer reports missing coverage instead of inventing expertise.</p></li>
                    <li><strong>Check what actually happened.</strong><p>Road Test grades bookings and receipts separately from the agent’s final message.</p></li>
                </ol>
            </div>}
        </div>
        <div className="editor-status"><span><FiGitBranch aria-hidden="true" /> main</span><span>{active.language} <span aria-hidden="true">·</span> UTF-8</span></div>
    </div>;
};

export const HeroSection = () => (
    <section className="hero coder-hero shell" aria-labelledby="hero-name">
        <div className="hero-copy">
            <p className="engineer-role"><FiCode aria-hidden="true" />{PROFILE.headline}</p>
            <h1 id="hero-name" aria-label={PROFILE.name}>Jarrod <span>Savard</span></h1>
            <h2>Zero-to-one products,<br />owned end to end.<span className="code-cursor" aria-hidden="true" /></h2>
            <p className="hero-summary">I build early-stage products from first prototype to production—shaping the frontend experience, designing the backend systems, and owning the engineering path between them.</p>
            <div className="hero-actions">
                <a className="action action-primary" href="#projects">Explore my work <FiArrowDownRight aria-hidden="true" /></a>
                <a className="text-link" href="#contact">Let’s talk <FiArrowUpRight aria-hidden="true" /></a>
            </div>
            <p className="hero-location"><FiMapPin aria-hidden="true" />{PROFILE.location} · Open to remote</p>
        </div>
        <EngineerNotebook />
        <div className="hero-foot">
            <p>Product engineering <span aria-hidden="true">·</span> Backend systems <span aria-hidden="true">·</span> AI / LLM</p>
            <a href={RESUME_PDF_PATH} download className="text-link">Download résumé <FiArrowDownRight aria-hidden="true" /></a>
        </div>
    </section>
);
